"use client";

import { RadioGroup } from "@ark-ui/react/radio-group";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, grid, stack } from "styled-system/patterns";

import { TradeLayout, VISIBLE_LAYOUTS } from "@/app/(main)/trade/[[...market-name]]/layout-config";

interface LayoutConfigurationsModalProps {
  currentLayout: TradeLayout;
  onSave: (layout: TradeLayout) => void;
}

export function openLayoutConfigurationsModal(props: LayoutConfigurationsModalProps) {
  openModal({
    id: "layout-configurations-modal",
    renderContent: (modalProps) => <LayoutConfigurationsModal {...modalProps} {...props} />,
  });
}

function LayoutConfigurationsModal({
  close,
  currentLayout,
  onSave,
}: ModalRenderProps & LayoutConfigurationsModalProps) {
  const t = useTranslations("layoutConfigurations");
  const generalT = useTranslations("general");
  const [selectedLayout, setSelectedLayout] = useState<TradeLayout>(currentLayout);

  const handleSave = () => {
    onSave(selectedLayout);
    close();
  };

  return (
    <ModalContent className={modalContentStyles}>
      <div className={stack({ gap: "12" })}>
        <h2 className={css({ textStyle: "heading.2xs", color: "text.secondary" })}>{t("title")}</h2>

        <RadioGroup.Root
          value={selectedLayout}
          onValueChange={(e) => setSelectedLayout(e.value as TradeLayout)}
          className={grid({
            gridTemplateColumns: "repeat(2, 1fr)",
            columnGap: "8",
            rowGap: "12",
          })}
        >
          {VISIBLE_LAYOUTS.map((layout) => (
            <RadioGroup.Item key={layout} value={layout} className={itemStyles}>
              <RadioGroup.ItemControl className={css({ srOnly: true })} />
              <RadioGroup.ItemText className={css({ flex: "1" })}>
                <LayoutThumbnail layout={layout} />
              </RadioGroup.ItemText>
              <RadioGroup.ItemHiddenInput />
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
      </div>

      <div className={flex({ gap: "4", justifyContent: "flex-end" })}>
        <Button variant="secondary" onClick={close}>
          {generalT("cancel")}
        </Button>
        <Button onClick={handleSave}>{generalT("save")}</Button>
      </div>
    </ModalContent>
  );
}

const modalContentStyles = stack({ w: "[443px]", gap: "16", p: "20" });

const itemStyles = css({
  cursor: "pointer",
  rounded: "200",
  borderWidth: "100",
  borderColor: "transparent",
  p: "0",
  overflow: "hidden",
  "&[data-state=checked]": {
    borderColor: "brandYellow.500",
  },
});

// ─── Layout Thumbnails ──────────────────────────────────────────────────────

function LayoutThumbnail({ layout }: { layout: TradeLayout }) {
  switch (layout) {
    case TradeLayout.DEFAULT:
      return (
        <div className={thumbnailContainer}>
          <ChartMini />
          <BookMini />
          <PanelMini />
        </div>
      );
    case TradeLayout.ORDERBOOK_LEFT:
      return (
        <div className={thumbnailContainer}>
          <BookMini />
          <ChartMini />
          <PanelMini />
        </div>
      );
    case TradeLayout.ORDER_FORM_LEFT:
      return (
        <div className={thumbnailContainer}>
          <PanelMini />
          <ChartMini />
          <BookMini />
        </div>
      );
    case TradeLayout.COMPACT_LEFT:
      return (
        <div className={thumbnailContainer}>
          <PanelMini />
          <BookMini />
          <ChartMini />
        </div>
      );
    case TradeLayout.CHART_FULL:
      return (
        <div className={thumbnailContainer}>
          <ChartMiniWide />
          <PanelMini />
        </div>
      );
    default: {
      const _exhaustive: never = layout;
      throw new Error(`Unknown layout: ${_exhaustive}`);
    }
  }
}

const thumbnailContainer = css({
  display: "flex",
  gap: "2",
  p: "2",
  h: "[82px]",
});

// ─── Mini Components ────────────────────────────────────────────────────────

const bullishBarStyles = css({
  position: "absolute",
  w: "[10px]",
  rounded: "50",
  bg: "background.positive",
});

const bearishBarStyles = css({
  position: "absolute",
  w: "[10px]",
  rounded: "50",
  bg: "background.negative",
});

const CANDLE_DATA = [
  { left: 2.5, top: 40.5, h: 20.5, bullish: true },
  { left: 14.5, top: 32, h: 17.5, bullish: true },
  { left: 26.5, top: 38, h: 15, bullish: false },
  { left: 38.5, top: 38, h: 20, bullish: false },
  { left: 50.5, top: 26, h: 23.5, bullish: true },
  { left: 62.5, top: 21, h: 17.5, bullish: true },
  { left: 74.5, top: 29.5, h: 17.5, bullish: false },
  { left: 86.5, top: 11.5, h: 27, bullish: true },
] as const;

const CANDLE_DATA_WIDE = [
  { left: 2.5, top: 50.5, h: 20.5, bullish: true },
  { left: 14.5, top: 42, h: 17.5, bullish: true },
  { left: 26.5, top: 48, h: 15, bullish: false },
  { left: 38.5, top: 48, h: 20, bullish: false },
  { left: 50.5, top: 36, h: 23.5, bullish: true },
  { left: 62.5, top: 31, h: 17.5, bullish: true },
  { left: 74.5, top: 39.5, h: 17.5, bullish: false },
  { left: 86.5, top: 21.5, h: 27, bullish: true },
  { left: 98, top: 39.5, h: 12, bullish: false },
  { left: 110, top: 16.5, h: 26, bullish: true },
  { left: 122, top: 8.5, h: 28, bullish: true },
  { left: 134, top: -0.5, h: 17, bullish: true },
] as const;

function ChartMini({ className }: { className?: string }) {
  return (
    <div className={cx(chartMiniStyles, className)}>
      {CANDLE_DATA.map((bar, i) => (
        <div
          key={i}
          style={{ left: `${bar.left}px`, top: `${bar.top}px`, height: `${bar.h}px` }}
          className={bar.bullish ? bullishBarStyles : bearishBarStyles}
        />
      ))}
    </div>
  );
}

function ChartMiniWide() {
  return (
    <div className={chartMiniWideStyles}>
      {CANDLE_DATA_WIDE.map((bar, i) => (
        <div
          key={i}
          style={{ left: `${bar.left}px`, top: `${bar.top}px`, height: `${bar.h}px` }}
          className={bar.bullish ? bullishBarStyles : bearishBarStyles}
        />
      ))}
    </div>
  );
}

const chartMiniBaseStyles = css.raw({
  bg: "background.primary",
  borderWidth: "[0.5px]",
  borderColor: "border.secondary",
  rounded: "50",
  overflow: "hidden",
  position: "relative",
});

const chartMiniStyles = css(chartMiniBaseStyles, {
  flex: "1",
  minW: "0",
});

const chartMiniWideStyles = css(chartMiniBaseStyles, {
  flex: "[1 0 0]",
  minW: "0",
});

function BookMini({ className }: { className?: string }) {
  return (
    <div className={cx(bookMiniStyles, className)}>
      <div className={css({ w: "full", h: "[10px]", rounded: "50", bg: "green.100" })} />
      <div className={css({ w: "[34.5px]", h: "[10px]", rounded: "50", bg: "green.100" })} />
      <div className={css({ w: "[21px]", h: "[10px]", rounded: "50", bg: "green.100" })} />
      <div className={css({ h: "[6px]" })} />
      <div className={css({ w: "[21px]", h: "[10px]", rounded: "50", bg: "text.negative" })} />
      <div className={css({ w: "[34.5px]", h: "[10px]", rounded: "50", bg: "text.negative" })} />
      <div className={css({ w: "full", h: "[10px]", rounded: "50", bg: "text.negative" })} />
    </div>
  );
}

const sidebarMiniBaseStyles = css.raw({
  w: "[45px]",
  flexShrink: 0,
  bg: "background.secondary",
  rounded: "50",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: "2",
});

const bookMiniStyles = css(sidebarMiniBaseStyles);

function PanelMini({ className }: { className?: string }) {
  return (
    <div className={cx(panelMiniStyles, className)}>
      <div className={css({ display: "flex", gap: "2" })}>
        <div className={css({ h: "[10px]", w: "[21.5px]", rounded: "50", bg: "green.100" })} />
        <div className={css({ h: "[10px]", w: "[21.5px]", rounded: "50", bg: "text.negative" })} />
      </div>
      <div className={css({ flex: "1", rounded: "50", bg: "background.secondary" })} />
      <div className={css({ h: "[10px]", rounded: "50", bg: "yellow.500" })} />
    </div>
  );
}

const panelMiniStyles = css(sidebarMiniBaseStyles);
